if (import.meta.hot) {
  import.meta.hot.accept();
}

import '@assets/Scss/component/accordion.scss';
import '../Scss/job-list.scss';
import 'bootstrap/js/dist/collapse';


// Filter

// Configuration parameters

const CONFIG = {
  selectors: {
    filterContainer: '.jobs-filters',
    accordionItem: '.accordion-item',
    accordionCollapse: '.accordion-collapse',
    checkbox: 'input[type="checkbox"]',
    jobItem: '.job',
    jobList: '.jobs-list',
    resetButton: '#filter-reset',
    noResults: '#no-results'
  },
  classes: {
    filtered: 'filtered',
    show: 'show'
  },
  attributes: {
    filterType: 'data-filter'
  }
};

// Main job filter functionality
class JobFilter {
  constructor(config) {
    this.config = config;
    this.filterContainer = document.querySelector(config.selectors.filterContainer);
    this.jobList = document.querySelector(config.selectors.jobList);
    this.resetButton = document.querySelector(config.selectors.resetButton);
    this.noResults = document.querySelector(config.selectors.noResults);
    this.init();
  }

  init() {
    if (!this.filterContainer || !this.jobList) {
      return;
    }

    this.attachEventListeners();
  }

  attachEventListeners() {
    // Attach change event to all checkboxes
    const checkboxes = this.filterContainer.querySelectorAll(this.config.selectors.checkbox);
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleCheckboxChange());
    });

    // Attach click event to reset button
    if (this.resetButton) {
      this.resetButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.resetFilters();
      });
    }
  }

  handleCheckboxChange() {
    // Get all accordion items (filter categories)
    const accordionItems = this.filterContainer.querySelectorAll(this.config.selectors.accordionItem);

    // Track if any checkbox is checked
    let hasActiveFilters = false;

    // Process each filter category independently
    accordionItems.forEach(accordionItem => {
      const filterType = accordionItem.getAttribute(this.config.attributes.filterType);
      if (!filterType) return;

      // Get all checked checkboxes in this category
      const checkedBoxes = accordionItem.querySelectorAll(`${this.config.selectors.checkbox}:checked`);

      if (checkedBoxes.length > 0) {
        hasActiveFilters = true;
        this.applyFilter(filterType, checkedBoxes);
      } else {
        // If no checkboxes are checked in this category, remove filter for this category
        this.removeFilterForCategory();
      }
    });

    // Show or hide reset button based on active filters
    this.toggleResetButton(hasActiveFilters);

    // Check if there are any visible results
    this.updateNoResultsVisibility();
  }

  applyFilter(filterType, checkedBoxes) {
    // Get all checked values for this filter category
    const checkedValues = Array.from(checkedBoxes).map(cb => cb.value);

    // Get all job items
    const jobItems = this.jobList.querySelectorAll(this.config.selectors.jobItem);

    jobItems.forEach(jobItem => {
      const jobDataValue = jobItem.getAttribute(`data-${filterType}`);

      if (!jobDataValue) {
        // If job doesn't have this data attribute, filter it out
        jobItem.classList.add(this.config.classes.filtered);
        return;
      }

      // Check if any of the checked values appears in the job's data attribute
      const hasMatch = checkedValues.some(value =>
        jobDataValue.split(',').map(v => v.trim()).includes(value)
      );

      if (!hasMatch) {
        jobItem.classList.add(this.config.classes.filtered);
      } else {
        // Only remove the filtered class if ALL active filter categories match
        if (this.jobMatchesAllActiveFilters(jobItem)) {
          jobItem.classList.remove(this.config.classes.filtered);
        }
      }
    });
  }

  removeFilterForCategory() {
    // When a category has no checked boxes, recalculate visibility for all jobs
    const jobItems = this.jobList.querySelectorAll(this.config.selectors.jobItem);

    jobItems.forEach(jobItem => {
      if (this.jobMatchesAllActiveFilters(jobItem)) {
        jobItem.classList.remove(this.config.classes.filtered);
      }
    });
  }

  jobMatchesAllActiveFilters(jobItem) {
    // Check if job matches all currently active filter categories
    const accordionItems = this.filterContainer.querySelectorAll(this.config.selectors.accordionItem);

    for (const accordionItem of accordionItems) {
      const filterType = accordionItem.getAttribute(this.config.attributes.filterType);
      if (!filterType) continue;

      const checkedBoxes = accordionItem.querySelectorAll(`${this.config.selectors.checkbox}:checked`);

      // If this category has checked boxes, verify the job matches at least one
      if (checkedBoxes.length > 0) {
        const checkedValues = Array.from(checkedBoxes).map(cb => cb.value);
        const jobDataValue = jobItem.getAttribute(`data-${filterType}`);

        if (!jobDataValue) return false;

        const hasMatch = checkedValues.some(value =>
          jobDataValue.split(',').map(v => v.trim()).includes(value)
        );

        if (!hasMatch) return false;
      }
    }

    return true;
  }

  toggleResetButton(show) {
    if (!this.resetButton) return;

    if (show) {
      this.resetButton.classList.add(this.config.classes.show);
    } else {
      this.resetButton.classList.remove(this.config.classes.show);
    }
  }

  updateNoResultsVisibility() {
    if (!this.noResults) return;

    // Get all job items
    const jobItems = this.jobList.querySelectorAll(this.config.selectors.jobItem);

    // Check if all jobs are filtered (have the filtered class)
    const allFiltered = Array.from(jobItems).every(jobItem =>
      jobItem.classList.contains(this.config.classes.filtered)
    );

    // Show no-results if all jobs are filtered, hide it otherwise
    if (allFiltered && jobItems.length > 0) {
      this.noResults.classList.add(this.config.classes.show);
    } else {
      this.noResults.classList.remove(this.config.classes.show);
    }
  }

  resetFilters() {
    // Uncheck all checkboxes
    const checkboxes = this.filterContainer.querySelectorAll(this.config.selectors.checkbox);
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Remove filtered class from all job items
    const jobItems = this.jobList.querySelectorAll(this.config.selectors.jobItem);
    jobItems.forEach(jobItem => {
      jobItem.classList.remove(this.config.classes.filtered);
    });

    // Close all accordions
    const accordionCollapses = this.filterContainer.querySelectorAll(this.config.selectors.accordionCollapse);
    accordionCollapses.forEach(collapse => {
      collapse.classList.remove('show');
    });

    // Hide reset button
    this.toggleResetButton(false);

    // Hide no-results message
    this.updateNoResultsVisibility();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new JobFilter(CONFIG);
});
